import { useCallback, useEffect, useState, type ReactNode } from 'react';
import {
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Skeleton,
  Tabs,
} from '@threatlens/design-system';
import {
  api,
  getSessionRoles,
  type AdminAnalysisStatus,
  type AdminUser,
  type QuarantineItem,
  type SourceControl,
  type ScalingConfig,
  type ScalingStateView,
  type ElasticHostView,
  type CostBreakdown,
  type CostPreview,
  type ScalingPatch,
  type GrowthFunnel,
  type CommunityModerationItem,
  type ResearcherVerificationView,
  type CommunityAccessStateView,
} from '../lib/api.js';
import { usePoll } from '../lib/use-poll.js';
import { asArray } from '../lib/as-array.js';
import { errMsg } from '../lib/errors.js';
import { Seo } from '../components/Seo.js';

type AdminTab = 'operations' | 'users' | 'platform' | 'infrastructure' | 'community';

const INPUT_CLS = 'w-full rounded border border-border-default bg-bg-base px-3 py-2 text-sm text-text-primary';

/**
 * Super-admin console. Operations (feed/queue/fleet/quarantine) is available to org operators;
 * the Users + Platform config tabs are platform-admin only (PLATFORM_ADMIN_EMAILS).
 */
export function AdminOpsPage() {
  const isPlatformAdmin = getSessionRoles().includes('platform-admin');
  const [tab, setTab] = useState<AdminTab>('operations');
  return (
    <>
      <Seo title="Platform operations" noindex />
      <div className="mx-auto max-w-6xl space-y-6">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h1 className="text-3xl font-bold text-text-primary">Admin console</h1>
            <p className="mt-1 text-text-secondary">
              {isPlatformAdmin ? 'Platform super-admin: operations, users, and configuration.' : 'Platform operations.'}
            </p>
          </div>
          {isPlatformAdmin && (
            <Tabs
              variant="pill"
              active={tab}
              onChange={(id) => setTab(id as AdminTab)}
              tabs={[
                { id: 'operations', label: 'operations' },
                { id: 'infrastructure', label: 'Infrastructure & Cost' },
                { id: 'users', label: 'users' },
                { id: 'platform', label: 'Platform config' },
                { id: 'community', label: 'Community exchange' },
              ]}
            />
          )}
        </div>
        {!isPlatformAdmin || tab === 'operations' ? (
          <OperationsTab />
        ) : tab === 'infrastructure' ? (
          <InfrastructureTab />
        ) : tab === 'users' ? (
          <UsersTab />
        ) : tab === 'community' ? (
          <CommunityModerationTab />
        ) : (
          <PlatformConfigTab />
        )}
      </div>
    </>
  );
}

/** Platform-operator console for feed, queue, fleet, quarantine, and spend controls. */
function OperationsTab() {
  const [status, setStatus] = useState<AdminAnalysisStatus | null>(null);
  const [sources, setSources] = useState<SourceControl[] | null>(null);
  const [quarantine, setQuarantine] = useState<QuarantineItem[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [sourcesNote, setSourcesNote] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [taskToCancel, setTaskToCancel] = useState('');
  const [hashToTakedown, setHashToTakedown] = useState('');

  // Each section loads independently: source-ingest (sources + quarantine) may be unreachable in
  // some deployments, but that must not blank out analysis status or the emergency controls.
  const refresh = useCallback(async () => {
    const [analysis, sourceResult, quarantineResult] = await Promise.allSettled([
      api.adminAnalysisStatus(),
      api.adminSources(),
      api.adminQuarantine(),
    ]);
    if (analysis.status === 'fulfilled') {
      setStatus(analysis.value);
      setError(null);
    } else {
      setError(errMsg(analysis.reason, 'Could not load analysis operations.'));
    }
    if (sourceResult.status === 'fulfilled') {
      setSources(sourceResult.value.sources);
      setSourcesNote(null);
    } else {
      setSources([]);
      setSourcesNote('Source-ingest controls are unavailable in this deployment.');
    }
    setQuarantine(quarantineResult.status === 'fulfilled' ? quarantineResult.value.items : []);
  }, []);

  // usePoll pauses while the tab is hidden; a backgrounded admin tab used to hit this every 5s
  // forever.
  usePoll(refresh, 5_000);

  const action = async (key: string, work: () => Promise<unknown>) => {
    setBusy(key);
    try {
      await work();
      await refresh();
    } catch (err) {
      setError(errMsg(err));
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="space-y-6">
      {error && <p className="rounded border border-status-danger/30 bg-status-danger/10 p-3 text-sm text-status-danger">{error}</p>}

      {/*
        Guests-busy and analyses-in-flight are DIFFERENT quantities and are shown separately.
        The old single "Detonation slots" tile combined them: its numerator fell back to a task count
        when guests were unmeasurable, and its denominator to a hardcoded 50 that corresponds to
        nothing, so it could render an impossible ratio. Neither figure invents a denominator now, and
        an unmeasurable tier reads "not measured" rather than 0 - 0 would look like spare capacity.
      */}
      <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
        <Metric label="Queued" value={status?.queued} />
        <Metric
          label="Guests in use"
          value={status ? formatGuestOccupancy(status) : undefined}
          hint={status ? guestOccupancyHint(status) : undefined}
        />
        <Metric
          label="Analyses in flight"
          value={
            status
              ? status.inFlight?.max != null
                ? `${status.inFlight.count}/${status.inFlight.max}`
                : String(status.inFlight?.count ?? status.running)
              : undefined
          }
          hint="Dynamic stage; exceeds guests in use by design"
        />
        <Metric
          label="Spend today"
          value={
            status?.fleet.spendTodayUsd !== undefined
              ? `$${status.fleet.spendTodayUsd.toFixed(2)}`
              : undefined
          }
        />
      </div>
      {status?.guests?.tiers?.length ? (
        <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-text-muted">
          {status.guests.tiers.map((t) => (
            <span key={t.tier}>
              {t.label}:{' '}
              <span className="text-text-secondary">
                {t.busy === null
                  ? 'not measured'
                  : t.capacity === null
                    ? `${t.busy} busy`
                    : `${t.busy}/${t.capacity}`}
              </span>
              {t.reason ? ` (${t.reason})` : ''}
            </span>
          ))}
        </div>
      ) : null}

      <Card>
        <CardHeader><CardTitle>Emergency controls</CardTitle></CardHeader>
        <CardContent className="flex flex-wrap gap-2">
          <Button
            variant="secondary"
            loading={busy === 'pause'}
            onClick={() => void action('pause', api.adminPauseAnalysis)}
          >
            Pause analysis admission
          </Button>
          <Button
            variant="secondary"
            loading={busy === 'resume'}
            onClick={() => void action('resume', api.adminResumeAnalysis)}
          >
            Resume analysis
          </Button>
          <span className="self-center text-xs text-text-muted">
            Network and fleet kill switches are enforced through SSM within 15 seconds.
          </span>
          <div className="mt-3 flex w-full flex-wrap gap-2 border-t border-border-subtle pt-3">
            <input
              value={taskToCancel}
              onChange={(event) => setTaskToCancel(event.target.value)}
              placeholder="Task ID to terminate"
              className="min-w-64 flex-1 rounded border border-border-default bg-bg-base px-3 py-2 font-mono text-sm text-text-primary"
            />
            <Button
              variant="secondary"
              disabled={!taskToCancel.trim()}
              loading={busy === 'cancel-task'}
              onClick={() =>
                void action('cancel-task', () =>
                  api.adminCancelTask(taskToCancel.trim()),
                )
              }
            >
              Terminate task
            </Button>
          </div>
          <div className="flex w-full flex-wrap gap-2">
            <input
              value={hashToTakedown}
              onChange={(event) => setHashToTakedown(event.target.value)}
              placeholder="SHA-256 to take down and deny"
              className="min-w-64 flex-1 rounded border border-border-default bg-bg-base px-3 py-2 font-mono text-sm text-text-primary"
            />
            <Button
              variant="danger"
              disabled={!/^[a-fA-F0-9]{64}$/.test(hashToTakedown.trim())}
              loading={busy === 'takedown'}
              onClick={() =>
                void action('takedown', () =>
                  api.adminTakedown(hashToTakedown.trim()),
                )
              }
            >
              Takedown + legal hold
            </Button>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader><CardTitle>Public sources</CardTitle></CardHeader>
        <CardContent>
          {sourcesNote && <p className="mb-3 text-sm text-text-muted">{sourcesNote}</p>}
          {!sources ? (
            <Skeleton className="h-24" />
          ) : sources.length === 0 && !sourcesNote ? (
            <p className="text-sm text-text-muted">No sources configured.</p>
          ) : (
            <div className="space-y-2">
              {sources.map((source) => (
                <div
                  key={source.source}
                  className="flex flex-wrap items-center gap-3 rounded border border-border-subtle p-3"
                >
                  <div className="min-w-40">
                    <div className="font-medium text-text-primary">{source.source}</div>
                    <div className="text-xs text-text-muted">
                      {source.lastSuccessAt ? `last success ${new Date(source.lastSuccessAt).toLocaleString()}` : 'not polled'}
                    </div>
                  </div>
                  <span className={source.paused || !source.enabled ? 'text-verdict-suspicious' : 'text-verdict-clean'}>
                    {source.paused ? 'paused' : source.enabled ? 'enabled' : 'disabled'}
                  </span>
                  <label className="flex items-center gap-2 text-xs text-text-muted">
                    batch
                    <input
                      type="number"
                      min={1}
                      max={1000}
                      value={source.maxItemsPerPoll}
                      onChange={(event) => {
                        const value = Math.max(
                          1,
                          Math.min(1000, Number(event.target.value) || 1),
                        );
                        setSources((current) =>
                          current?.map((item) =>
                            item.source === source.source
                              ? { ...item, maxItemsPerPoll: value }
                              : item,
                          ) ?? null,
                        );
                      }}
                      onBlur={() =>
                        void action(`rate-${source.source}`, () =>
                          api.adminUpdateSource(source.source, {
                            maxItemsPerPoll: source.maxItemsPerPoll,
                          }),
                        )
                      }
                      className="w-20 rounded border border-border-default bg-bg-base px-2 py-1 text-text-primary"
                    />
                  </label>
                  <div className="ml-auto flex gap-2">
                    <Button
                      size="sm"
                      variant="secondary"
                      loading={busy === `toggle-${source.source}`}
                      onClick={() =>
                        void action(`toggle-${source.source}`, () =>
                          api.adminUpdateSource(source.source, {
                            paused: !source.paused,
                          }),
                        )
                      }
                    >
                      {source.paused ? 'Resume' : 'Pause'}
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      loading={busy === `poll-${source.source}`}
                      onClick={() =>
                        void action(`poll-${source.source}`, () =>
                          api.adminPollSource(source.source),
                        )
                      }
                    >
                      Poll now
                    </Button>
                  </div>
                  {source.lastError && <p className="w-full text-xs text-status-danger">{source.lastError}</p>}
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader><CardTitle>Quarantine · {quarantine?.length ?? 0}</CardTitle></CardHeader>
        <CardContent>
          {!quarantine ? (
            <Skeleton className="h-24" />
          ) : quarantine.length === 0 ? (
            <p className="text-sm text-text-muted">No quarantined source items.</p>
          ) : (
            <div className="max-h-80 space-y-2 overflow-y-auto">
              {quarantine.map((item) => (
                <div key={item.id} className="rounded border border-border-subtle p-3 text-sm">
                  <div className="flex justify-between gap-3">
                    <span className="font-mono text-text-primary">{item.externalId}</span>
                    <span className="text-text-muted">{item.source}</span>
                  </div>
                  <p className="mt-1 text-xs text-status-danger">{item.reason}</p>
                  <div className="mt-2 flex gap-2">
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() =>
                        void action(`resolve-${item.id}`, () =>
                          api.adminUpdateQuarantine(item.id, 'resolved'),
                        )
                      }
                    >
                      Mark resolved
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() =>
                        void action(`discard-${item.id}`, () =>
                          api.adminUpdateQuarantine(item.id, 'discarded'),
                        )
                      }
                    >
                      Discard
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

/** Platform-admin: search users, edit status/role, set per-user daily quota, revoke keys/sessions. */
function UsersTab() {
  const [users, setUsers] = useState<AdminUser[] | null>(null);
  const [search, setSearch] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [quotaDraft, setQuotaDraft] = useState<Record<string, string>>({});

  const refresh = useCallback(async (q: string) => {
    try {
      const res = await api.adminListUsers(q, 200);
      setUsers(asArray(res.data));
      setError(null);
    } catch (err) {
      setError(errMsg(err));
      setUsers([]);
    }
  }, []);

  useEffect(() => {
    void refresh('');
  }, [refresh]);

  const act = async (key: string, work: () => Promise<unknown>) => {
    setBusy(key);
    try {
      await work();
      await refresh(search);
    } catch (err) {
      setError(errMsg(err));
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="space-y-4">
      {error && <p className="rounded border border-status-danger/30 bg-status-danger/10 p-3 text-sm text-status-danger">{error}</p>}
      <form
        onSubmit={(e) => {
          e.preventDefault();
          void refresh(search);
        }}
        className="flex gap-2"
      >
        <input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search by email, name, or user id"
          className="flex-1 rounded border border-border-default bg-bg-base px-3 py-2 text-sm text-text-primary"
        />
        <Button type="submit" variant="secondary">Search</Button>
      </form>

      {!users ? (
        <Skeleton className="h-40" />
      ) : users.length === 0 ? (
        <p className="text-sm text-text-muted">No users found.</p>
      ) : (
        <div className="space-y-2">
          {users.map((u) => (
            <div key={u.id} className="rounded border border-border-subtle p-3">
              <div className="flex flex-wrap items-center gap-3">
                <div className="min-w-56 flex-1">
                  <div className="font-medium text-text-primary">{u.email}</div>
                  <div className="text-xs text-text-muted">
                    {u.displayName} · {u.orgs[0]?.roles.join(', ') || 'no role'} · {u.apiKeys} keys
                    {u.mfaEnabled ? ' · MFA' : ''}
                  </div>
                </div>
                <select
                  value={u.status}
                  disabled={busy !== null}
                  onChange={(e) => void act(`status-${u.id}`, () => api.adminUpdateUser(u.id, { status: e.target.value }))}
                  className="rounded border border-border-default bg-bg-base px-2 py-1 text-sm text-text-primary"
                >
                  {['active', 'suspended', 'locked', 'deactivated'].map((s) => (
                    <option key={s} value={s}>{s}</option>
                  ))}
                </select>
                {u.orgs[0] && (
                  <select
                    value={u.orgs[0].roles[0] ?? 'analyst'}
                    disabled={busy !== null}
                    onChange={(e) =>
                      void act(`role-${u.id}`, () => api.adminUpdateUser(u.id, { orgId: u.orgs[0]!.orgId, roles: [e.target.value] }))
                    }
                    className="rounded border border-border-default bg-bg-base px-2 py-1 text-sm text-text-primary"
                  >
                    {['owner', 'admin', 'analyst', 'read-only', 'billing'].map((r) => (
                      <option key={r} value={r}>{r}</option>
                    ))}
                  </select>
                )}
              </div>
              <div className="mt-2 flex flex-wrap items-center gap-2 border-t border-border-subtle pt-2">
                <label className="flex items-center gap-2 text-xs text-text-muted">
                  daily lookups
                  <input
                    type="number"
                    value={quotaDraft[u.id] ?? (u.dailyLookupQuota ?? '')}
                    placeholder={String(u.effectiveDailyLookupQuota)}
                    onChange={(e) => setQuotaDraft((d) => ({ ...d, [u.id]: e.target.value }))}
                    className="w-28 rounded border border-border-default bg-bg-base px-2 py-1 text-text-primary"
                  />
                </label>
                <Button
                  size="sm"
                  variant="secondary"
                  loading={busy === `quota-${u.id}`}
                  onClick={() => {
                    const raw = quotaDraft[u.id];
                    const val = raw === undefined || raw === '' ? null : Number(raw);
                    void act(`quota-${u.id}`, () => api.adminSetUserLimits(u.id, val));
                  }}
                >
                  Set quota
                </Button>
                <span className="text-xs text-text-muted">effective {u.effectiveDailyLookupQuota < 0 ? '∞' : u.effectiveDailyLookupQuota}/day</span>
                <div className="ml-auto flex gap-2">
                  <Button size="sm" variant="ghost" loading={busy === `keys-${u.id}`} onClick={() => void act(`keys-${u.id}`, () => api.adminRevokeKeys(u.id))}>
                    Revoke keys
                  </Button>
                  <Button size="sm" variant="ghost" loading={busy === `ses-${u.id}`} onClick={() => void act(`ses-${u.id}`, () => api.adminRevokeSessions(u.id))}>
                    Revoke sessions
                  </Button>
                  <Button size="sm" variant="ghost" loading={busy === `mfa-${u.id}`} onClick={() => void act(`mfa-${u.id}`, () => api.adminResetMfa(u.id))}>
                    Reset MFA
                  </Button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

const SCALE_DIMENSIONS: { key: keyof ScalingConfig & string; label: string; help: string }[] = [
  { key: 'detonationGuests', label: 'Parallel detonations', help: 'Concurrent live malware detonations (guest slots).' },
  { key: 'detonationHosts', label: 'Detonation hosts', help: 'Bare-metal QEMU hosts (each is the big cost lever).' },
  { key: 'analysisWorkers', label: 'Analysis workers', help: 'SQS consumers - processing speed.' },
  { key: 'apiTasks', label: 'API tasks', help: 'Front-of-house API/SPA task count.' },
  { key: 'openSearchNodes', label: 'Search nodes', help: 'OpenSearch data nodes - search backend infra.' },
  { key: 'redisNodes', label: 'Cache nodes', help: 'Redis metering/cache tier.' },
];

/**
 * Infrastructure & Cost (spec Workstream C-F): live cost, per-dimension scaling with projected-cost
 * previews, hard budget ceiling, apply-with-convergence, and the auto-scale master toggle. Lets the
 * admin resize the platform (detonations, compute, storage, search, DB) from the dashboard with
 * cost visibility - no codebase changes.
 */
function InfrastructureTab() {
  const [config, setConfig] = useState<ScalingConfig | null>(null);
  const [cost, setCost] = useState<CostBreakdown | null>(null);
  const [scalingState, setScalingState] = useState<ScalingStateView | null>(null);
  const [draft, setDraft] = useState<Record<string, number>>({});
  const [preview, setPreview] = useState<CostPreview | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [saved, setSaved] = useState(false);

  const load = useCallback(async () => {
    try {
      const r = await api.adminScalingState();
      setConfig(r.config);
      setCost(r.cost);
      setScalingState(r.state);
      setError(null);
    } catch (err) {
      setError(errMsg(err));
    }
  }, []);

  useEffect(() => {
    void load();
    const t = setInterval(() => void load(), 10_000);
    return () => clearInterval(t);
  }, [load]);

  // Build a patch from the current draft (desired overrides) + toggles.
  const buildPatch = useCallback((): ScalingPatch => {
    const patch: ScalingPatch = {};
    for (const d of SCALE_DIMENSIONS) {
      const v = draft[d.key];
      if (v !== undefined) (patch as Record<string, unknown>)[d.key] = { desired: v };
    }
    if (draft.dailyBudgetUsd !== undefined) patch.dailyBudgetUsd = draft.dailyBudgetUsd;
    if (draft.monthlyBudgetUsd !== undefined) patch.monthlyBudgetUsd = draft.monthlyBudgetUsd;
    if (draft.autoScaleEnabled !== undefined) patch.autoScaleEnabled = draft.autoScaleEnabled === 1;
    return patch;
  }, [draft]);

  // Debounced projected-cost preview as the admin edits.
  useEffect(() => {
    if (Object.keys(draft).length === 0) {
      setPreview(null);
      return;
    }
    let cancelled = false;
    const h = setTimeout(() => {
      api
        .adminScalingPreview(buildPatch())
        .then((p) => {
          if (!cancelled) setPreview(p);
        })
        .catch(() => {
          if (!cancelled) setPreview(null);
        });
    }, 400);
    return () => {
      cancelled = true;
      clearTimeout(h);
    };
  }, [draft, buildPatch]);

  const apply = async () => {
    setBusy(true);
    setSaved(false);
    setError(null);
    try {
      const r = await api.adminApplyScaling(buildPatch());
      setConfig(r.config);
      setCost(r.cost);
      setDraft({});
      setPreview(null);
      setSaved(true);
      await load();
    } catch (err) {
      setError(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  // Error first: `config` is also null on a failed load, so a skeleton guard placed above this
  // would make the banner below unreachable (the original defect).
  if (error && (!config || !cost)) return <PanelError error={error} onRetry={() => void load()} />;
  if (!config || !cost) return <Skeleton className="h-64" />;
  const shownCost = preview?.proposed ?? cost;
  const autoScale = draft.autoScaleEnabled !== undefined ? draft.autoScaleEnabled === 1 : config.autoScaleEnabled;
  const dailyBudget = draft.dailyBudgetUsd ?? config.dailyBudgetUsd;

  return (
    <div className="space-y-4">
      {error && <p className="rounded border border-status-danger/30 bg-status-danger/10 p-3 text-sm text-status-danger">{error}</p>}

      <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
        <Metric label="Run-rate /mo" value={`$${shownCost.monthlyUsd.toLocaleString()}`} />
        <Metric label="Run-rate /day" value={`$${shownCost.dailyUsd.toLocaleString()}`} />
        <Metric label="Daily budget" value={`$${dailyBudget.toLocaleString()}`} />
        <Metric label="Auto-scale" value={autoScale ? 'ON' : 'OFF'} />
      </div>

      <Card>
        <CardHeader><CardTitle>Cost breakdown (estimate)</CardTitle></CardHeader>
        <CardContent className="space-y-1">
          {shownCost.lines.map((l) => (
            <div key={l.label} className="flex justify-between border-b border-border-subtle py-1 text-sm last:border-0">
              <span className="text-text-secondary">{l.label}</span>
              <span className="font-mono text-text-primary">${l.monthlyUsd.toLocaleString()}/mo</span>
            </div>
          ))}
        </CardContent>
      </Card>

      {scalingState?.elastic && <ElasticHostCard elastic={scalingState.elastic} />}

      <Card>
        <CardHeader><CardTitle>Scale the platform</CardTitle></CardHeader>
        <CardContent className="space-y-3">
          {SCALE_DIMENSIONS.map((d) => {
            const dim = config[d.key] as { desired: number; min: number; max: number };
            const val = draft[d.key] ?? dim.desired;
            return (
              <div key={d.key} className="flex flex-wrap items-center gap-3 rounded border border-border-subtle p-3">
                <div className="min-w-52 flex-1">
                  <div className="font-medium text-text-primary">{d.label}</div>
                  <div className="text-xs text-text-muted">{d.help} (min {dim.min}, max {dim.max})</div>
                </div>
                <input
                  type="range"
                  min={dim.min}
                  max={dim.max}
                  value={val}
                  onChange={(e) => setDraft((s) => ({ ...s, [d.key]: Number(e.target.value) }))}
                  className="w-40"
                />
                <input
                  type="number"
                  min={dim.min}
                  max={dim.max}
                  value={val}
                  onChange={(e) => setDraft((s) => ({ ...s, [d.key]: Number(e.target.value) }))}
                  className="w-24 rounded border border-border-default bg-bg-base px-2 py-1 text-sm text-text-primary"
                />
              </div>
            );
          })}
          <div className="grid gap-3 sm:grid-cols-2">
            <label className="block">
              <span className="text-sm text-text-secondary">Daily budget ceiling ($)</span>
              <input
                type="number"
                value={draft.dailyBudgetUsd ?? config.dailyBudgetUsd}
                onChange={(e) => setDraft((s) => ({ ...s, dailyBudgetUsd: Number(e.target.value) }))}
                className={`mt-1 ${INPUT_CLS}`}
              />
            </label>
            <label className="block">
              <span className="text-sm text-text-secondary">Monthly budget ceiling ($)</span>
              <input
                type="number"
                value={draft.monthlyBudgetUsd ?? config.monthlyBudgetUsd}
                onChange={(e) => setDraft((s) => ({ ...s, monthlyBudgetUsd: Number(e.target.value) }))}
                className={`mt-1 ${INPUT_CLS}`}
              />
            </label>
          </div>
          <label className="flex items-center gap-2 text-sm text-text-secondary">
            <input
              type="checkbox"
              checked={autoScale}
              onChange={(e) => setDraft((s) => ({ ...s, autoScaleEnabled: e.target.checked ? 1 : 0 }))}
            />
            Auto-scale (platform grows/shrinks itself within these min/max bounds and the budget ceiling)
          </label>

          {preview && (
            <div className={`rounded border p-3 text-sm ${preview.withinDailyBudget && preview.withinMonthlyBudget ? 'border-accent-500/40 bg-accent-500/10' : 'border-status-danger/40 bg-status-danger/10'}`}>
              Projected change: <span className="font-mono">{preview.deltaMonthlyUsd >= 0 ? '+' : ''}${preview.deltaMonthlyUsd.toLocaleString()}/mo</span>{' '}
              (<span className="font-mono">{preview.deltaDailyUsd >= 0 ? '+' : ''}${preview.deltaDailyUsd.toLocaleString()}/day</span>) → new run-rate ${preview.proposed.monthlyUsd.toLocaleString()}/mo.
              {preview.budgetMessage && <div className="mt-1 text-status-danger">{preview.budgetMessage}</div>}
            </div>
          )}

          <div className="flex items-center gap-3">
            <Button
              loading={busy}
              disabled={Object.keys(draft).length === 0 || (!!preview && (!preview.withinDailyBudget || !preview.withinMonthlyBudget))}
              onClick={() => void apply()}
            >
              Apply &amp; scale
            </Button>
            {saved && <span className="text-sm text-verdict-clean">Applied. Reconciling within ~30s.</span>}
            <span className="text-xs text-text-muted">Changes take effect live; detonation caps within ~30s, infra within a few minutes.</span>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

/**
 * Elastic (pay-per-use) shared detonation host panel. Surfaces the controller-observed lifecycle
 * state, today's accrued up-hours + pay-per-use cost, the per-class detonation demand driving the
 * lifecycle, and the SSM master switch. All values are observed by the scaling-controller (never
 * fabricated); the card only renders when the controller has written a runtime block.
 */
function ElasticHostCard({ elastic }: { elastic: ElasticHostView }) {
  const lifecycleColor =
    elastic.lifecycleState === 'active' || elastic.lifecycleState === 'draining'
      ? 'text-verdict-clean'
      : elastic.lifecycleState === 'stopped'
        ? 'text-text-muted'
        : 'text-verdict-suspicious';
  const fmtTime = (iso: string | null) => (iso ? new Date(iso).toLocaleString() : 'none');
  return (
    <Card>
      <CardHeader>
        <CardTitle>Elastic detonation host (pay-per-use)</CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="flex flex-wrap items-center gap-3 text-sm">
          <span className={`font-semibold capitalize ${lifecycleColor}`}>
            {elastic.lifecycleState.replace('_', ' ')}
          </span>
          <span className="text-text-muted">EC2: {elastic.ec2State}</span>
          <span className={elastic.enabled ? 'text-verdict-clean' : 'text-text-muted'}>
            lifecycle {elastic.enabled ? 'ENABLED' : 'OFF'}
          </span>
          {elastic.allowOverBudget && <span className="text-verdict-suspicious">over-budget override</span>}
        </div>
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
          <Metric label="Up-hours today" value={elastic.upHoursToday.toFixed(2)} />
          <Metric label="Cost today" value={`$${elastic.costTodayUsd.toFixed(2)}`} />
          <Metric label="Projected /mo" value={`$${elastic.projectedMonthlyUsd.toLocaleString()}`} />
          <Metric label="Rate" value={`$${elastic.hourlyUsd.toFixed(2)}/hr`} />
        </div>
        <div className="rounded border border-border-subtle p-3 text-sm">
          <div className="mb-1 text-xs uppercase text-text-muted">Detonation demand (drives lifecycle)</div>
          <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
            <span className="text-text-secondary">mobile-app queued: <span className="font-mono text-text-primary">{elastic.demand.androidQueued}</span></span>
            <span className="text-text-secondary">mobile-app running: <span className="font-mono text-text-primary">{elastic.demand.androidRunning}</span></span>
            <span className="text-text-secondary">binary queued: <span className="font-mono text-text-primary">{elastic.demand.binaryQueued}</span></span>
            <span className="text-text-secondary">binary running: <span className="font-mono text-text-primary">{elastic.demand.binaryRunning}</span></span>
          </div>
        </div>
        <div className="text-xs text-text-muted">
          Pay-per-use: cost accrues only while the box is running (~${elastic.hourlyUsd.toFixed(2)}/hr, c5.metal). Last
          start {fmtTime(elastic.lastStartAt)} · last stop {fmtTime(elastic.lastStopAt)} · updated {fmtTime(elastic.updatedAt)}.
        </div>
      </CardContent>
    </Card>
  );
}

/** Platform-admin: default lookup quota, reward policy, registration + kill switches. */
/** Referral + acquisition funnel (reads growth-svc analytics with the platform-admin session). */
function GrowthPanel() {
  const [funnel, setFunnel] = useState<GrowthFunnel | null>(null);
  const [sources, setSources] = useState<Record<string, number> | null>(null);
  const [err, setErr] = useState(false);
  useEffect(() => {
    api.growthFunnel().then(setFunnel).catch(() => setErr(true));
    api.growthSources().then(setSources).catch(() => undefined);
  }, []);
  const tiles: [string, string | number][] = funnel
    ? [
        ['Signups', funnel.signups.toLocaleString()],
        ['Invites', funnel.invitesSent.toLocaleString()],
        ['Referrals', funnel.referralsConverted.toLocaleString()],
        ['Shares', funnel.shares.toLocaleString()],
        ['K-factor', funnel.kFactor],
      ]
    : [];
  return (
    <Card>
      <CardHeader><CardTitle>Growth: referrals &amp; funnel</CardTitle></CardHeader>
      <CardContent className="space-y-3">
        {err && <p className="text-sm text-text-secondary">Growth service unavailable (not deployed or unreachable).</p>}
        {funnel && (
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-5">
            {tiles.map(([label, value]) => (
              <div key={label} className="rounded border border-border-subtle bg-bg-base/40 p-3">
                <div className="text-xl font-bold text-text-primary">{value}</div>
                <div className="text-micro text-text-muted">{label}</div>
              </div>
            ))}
          </div>
        )}
        {sources && Object.keys(sources).length > 0 && (
          <div className="text-xs text-text-muted">
            Signup sources: {Object.entries(sources).map(([s, n]) => `${s} (${n})`).join(' \u00b7 ')}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function PlatformConfigTab() {
  const [settings, setSettings] = useState<Record<string, string> | null>(null);
  const [draft, setDraft] = useState<Record<string, string>>({});
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [saved, setSaved] = useState(false);

  const load = useCallback(() => {
    setError(null);
    api
      .adminGetPlatformSettings()
      .then((r) => {
        setSettings(r.settings);
        setDraft(r.settings);
      })
      .catch((err) => setError(errMsg(err)));
  }, []);

  useEffect(() => load(), [load]);

  const field = (key: string, fallback = '') => draft[key] ?? settings?.[key] ?? fallback;
  const set = (key: string, value: string) => setDraft((d) => ({ ...d, [key]: value }));

  const save = async () => {
    setBusy(true);
    setSaved(false);
    try {
      const r = await api.adminSetPlatformSettings(draft);
      setSettings(r.settings);
      setSaved(true);
    } catch (err) {
      setError(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  if (error && !settings) return <PanelError error={error} onRetry={() => void load()} />;
  if (!settings) return <Skeleton className="h-40" />;

  return (
    <div className="space-y-4">
      {error && <p className="rounded border border-status-danger/30 bg-status-danger/10 p-3 text-sm text-status-danger">{error}</p>}
      <GrowthPanel />
      <Card>
        <CardHeader><CardTitle>Quotas &amp; rewards</CardTitle></CardHeader>
        <CardContent className="grid gap-4 sm:grid-cols-2">
          <ConfigField label="Default daily lookup quota" hint="-1 = unlimited">
            <input type="number" value={field('default_daily_lookup_quota', '100')} onChange={(e) => set('default_daily_lookup_quota', e.target.value)} className={INPUT_CLS} />
          </ConfigField>
          <ConfigField label="Bonus lookups / unique sample" hint="extra daily dataset lookups earned per contributed sample (not wallet credits)">
            <input type="number" value={field('reward_credits_per_unique_sample', '1')} onChange={(e) => set('reward_credits_per_unique_sample', e.target.value)} className={INPUT_CLS} />
          </ConfigField>
          <ConfigField label="Bonus lookups daily cap">
            <input type="number" value={field('reward_daily_cap', '100000')} onChange={(e) => set('reward_daily_cap', e.target.value)} className={INPUT_CLS} />
          </ConfigField>
          <ConfigField label="Rewards enabled">
            <select value={field('reward_enabled', 'true')} onChange={(e) => set('reward_enabled', e.target.value)} className={INPUT_CLS}>
              <option value="true">enabled</option>
              <option value="false">disabled</option>
            </select>
          </ConfigField>
        </CardContent>
      </Card>
      <Card>
        <CardHeader><CardTitle>Credit pricing (billing)</CardTitle></CardHeader>
        <CardContent className="grid gap-4 sm:grid-cols-2">
          <ConfigField label="Private detonation (credits)" hint="charged per authenticated private sample; blank = default 40">
            <input type="number" value={field('credit_price_detonation', '40')} onChange={(e) => set('credit_price_detonation', e.target.value)} className={INPUT_CLS} />
          </ConfigField>
          <ConfigField label="Deep URL scan (credits)" hint="browser deep pass; fast pass is free; default 20">
            <input type="number" value={field('credit_price_deep_scan', '20')} onChange={(e) => set('credit_price_deep_scan', e.target.value)} className={INPUT_CLS} />
          </ConfigField>
          <ConfigField label="Interactive VM (credits / min)" hint="default 10">
            <input type="number" value={field('credit_price_interactive_minute', '10')} onChange={(e) => set('credit_price_interactive_minute', e.target.value)} className={INPUT_CLS} />
          </ConfigField>
          <ConfigField label="Retrohunt (credits / job)" hint="default 300">
            <input type="number" value={field('credit_price_retrohunt', '300')} onChange={(e) => set('credit_price_retrohunt', e.target.value)} className={INPUT_CLS} />
          </ConfigField>
          <ConfigField label="Livehunt (credits)" hint="standing rule; default 100">
            <input type="number" value={field('credit_price_livehunt', '100')} onChange={(e) => set('credit_price_livehunt', e.target.value)} className={INPUT_CLS} />
          </ConfigField>
          <ConfigField label="API data-plane call (credits)" hint="per authenticated API-key call; default 1">
            <input type="number" value={field('credit_price_api_call', '1')} onChange={(e) => set('credit_price_api_call', e.target.value)} className={INPUT_CLS} />
          </ConfigField>
          <ConfigField label="Free detonations / day" hint="public/anon daily cap (free but bounded); -1 = unlimited; default 20">
            <input type="number" value={field('daily_detonation_cap', '20')} onChange={(e) => set('daily_detonation_cap', e.target.value)} className={INPUT_CLS} />
          </ConfigField>
        </CardContent>
      </Card>
      <Card>
        <CardHeader><CardTitle>Access &amp; safety</CardTitle></CardHeader>
        <CardContent className="grid gap-4 sm:grid-cols-2">
          <ConfigField label="Self-serve registration">
            <select value={field('registration_open', 'true')} onChange={(e) => set('registration_open', e.target.value)} className={INPUT_CLS}>
              <option value="true">open</option>
              <option value="false">closed</option>
            </select>
          </ConfigField>
          <ConfigField label="Require platform-first for rewards">
            <select value={field('reward_require_platform_first', 'false')} onChange={(e) => set('reward_require_platform_first', e.target.value)} className={INPUT_CLS}>
              <option value="false">no</option>
              <option value="true">yes</option>
            </select>
          </ConfigField>
        </CardContent>
      </Card>
      <Card>
        <CardHeader><CardTitle>Detection engine &amp; verdict</CardTitle></CardHeader>
        <CardContent className="grid gap-4 sm:grid-cols-2">
          <ConfigField label="Verdict weights version" hint="stamped on every verdict for reproducibility">
            <input value={field('verdict_weights_version', '2026-07-15')} onChange={(e) => set('verdict_weights_version', e.target.value)} className={INPUT_CLS} />
          </ConfigField>
          <ConfigField label="Malicious threshold" hint="score >= this => malicious (default 70)">
            <input type="number" value={field('verdict_malicious_threshold', '70')} onChange={(e) => set('verdict_malicious_threshold', e.target.value)} className={INPUT_CLS} />
          </ConfigField>
          <ConfigField label="Suspicious threshold" hint="score >= this => suspicious (default 35)">
            <input type="number" value={field('verdict_suspicious_threshold', '35')} onChange={(e) => set('verdict_suspicious_threshold', e.target.value)} className={INPUT_CLS} />
          </ConfigField>
          <ConfigField label="Clean coverage floor" hint="min coverage 0-1 to assert clean, else insufficient">
            <input type="number" step="0.05" value={field('verdict_clean_coverage_floor', '0.5')} onChange={(e) => set('verdict_clean_coverage_floor', e.target.value)} className={INPUT_CLS} />
          </ConfigField>
          <ConfigField label="Disabled analyzers" hint="comma-separated engine ids (e.g. peid,pdfid)">
            <input value={field('analyzer.disabled', '')} onChange={(e) => set('analyzer.disabled', e.target.value)} className={INPUT_CLS} />
          </ConfigField>
          <ConfigField label="Active ML model version" hint="pins the triage model (Track D)">
            <input value={field('ml.activeModelVersion', '')} onChange={(e) => set('ml.activeModelVersion', e.target.value)} className={INPUT_CLS} />
          </ConfigField>
        </CardContent>
      </Card>
      <div className="flex items-center gap-3">
        <Button loading={busy} onClick={() => void save()}>Save configuration</Button>
        {saved && <span className="text-sm text-verdict-clean">Saved. Effective within ~30s.</span>}
      </div>
    </div>
  );
}

function ConfigField({ label, hint, children }: { label: string; hint?: string; children: ReactNode }) {
  return (
    <label className="block">
      <span className="text-sm text-text-secondary">{label}</span>
      {hint && <span className="ml-2 text-xs text-text-muted">{hint}</span>}
      <div className="mt-1">{children}</div>
    </label>
  );
}

/**
 * Panel-level load failure. Both panels below used to early-return a <Skeleton /> whenever their
 * data was null, which a failed fetch also satisfies - so the error banner further down the JSX was
 * unreachable and the panel hung on a pulsing grey box with no message and no retry. Render this
 * BEFORE any skeleton guard.
 */
function PanelError({ error, onRetry }: { error: string; onRetry?: () => void }) {
  return (
    <div
      role="alert"
      className="space-y-3 rounded border border-status-danger/30 bg-status-danger/10 p-4 text-sm text-status-danger"
    >
      <p>{error}</p>
      {onRetry && (
        <Button variant="secondary" size="sm" onClick={onRetry}>
          Try again
        </Button>
      )}
    </div>
  );
}

function Metric({ label, value, hint }: { label: string; value: string | number | undefined; hint?: string }) {
  return (
    <Card>
      <CardContent className="py-4">
        <div className="text-xs uppercase text-text-muted">{label}</div>
        <div className="mt-1 text-xl font-semibold text-text-primary">
          {value ?? 'n/a'}
        </div>
        {hint ? <div className="mt-1 text-xs text-text-muted">{hint}</div> : null}
      </CardContent>
    </Card>
  );
}

/**
 * Measured guests-busy, shown against a total ONLY when every reporting tier stated one. A partial
 * reading gives the count alone, because summing part of the fleet's guests under a full-fleet
 * denominator is how a >100% figure gets published.
 */
function formatGuestOccupancy(status: AdminAnalysisStatus): string {
  const g = status.guests;
  if (!g || g.busy === null) return 'not measured';
  if (g.capacity === null || !g.complete) return `${g.busy} busy`;
  return `${g.busy}/${g.capacity}`;
}

function guestOccupancyHint(status: AdminAnalysisStatus): string | undefined {
  const g = status.guests;
  if (!g) return undefined;
  if (g.busy === null) return 'No detonation host reported occupancy';
  if (g.capacity === null || !g.complete) return 'Partial fleet: no total published';
  // Withheld above capacity rather than clamped: >100% is a measurement fault worth seeing raw.
  if (g.busy > g.capacity) return 'Reading exceeds capacity; percentage withheld';
  return `${Math.round((g.busy / g.capacity) * 100)}% of measured guests`;
}

/**
 * Sample Vault Phase-4 community-exchange moderation (platform-admin). Three surfaces: the pending
 * contribution moderation queue (approve / reject / request-changes), verified-researcher management
 * (grant / reject / revoke), and abuse/suspension controls (suspend / restore / clear-flag). All
 * actions call analysis-svc's platform-admin routes and are audited server-side.
 */
function CommunityModerationTab() {
  const [queue, setQueue] = useState<CommunityModerationItem[] | null>(null);
  const [researchers, setResearchers] = useState<ResearcherVerificationView[] | null>(null);
  const [access, setAccess] = useState<CommunityAccessStateView[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    setError(null);
    const [q, r, a] = await Promise.allSettled([
      api.adminCommunityModeration(100),
      api.adminCommunityResearchers(),
      api.adminCommunityAccess({}),
    ]);
    if (q.status === 'fulfilled') setQueue(q.value.data);
    else {
      setQueue([]);
      setError(errMsg(q.reason, 'Could not load the moderation queue (community exchange may be disabled).'));
    }
    setResearchers(r.status === 'fulfilled' ? r.value.data : []);
    setAccess(a.status === 'fulfilled' ? a.value.data : []);
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  async function act(key: string, fn: () => Promise<unknown>) {
    setBusy(key);
    setError(null);
    try {
      await fn();
      await refresh();
    } catch (err) {
      setError(errMsg(err, 'Action failed'));
    } finally {
      setBusy(null);
    }
  }

  return (
    <div className="space-y-6">
      {error && <div className="rounded-md border border-status-danger/40 bg-status-danger/5 px-3 py-2 text-sm text-status-danger">{error}</div>}

      <Card>
        <CardHeader>
          <CardTitle>Moderation queue{queue ? ` (${queue.length})` : ''}</CardTitle>
        </CardHeader>
        <CardContent>
          {!queue ? (
            <Skeleton className="h-16 w-full" />
          ) : queue.length === 0 ? (
            <div className="py-6 text-center text-sm text-text-muted">No contributions awaiting review.</div>
          ) : (
            <div className="divide-y divide-white/5">
              {queue.map((c) => (
                <div key={c.id} className="py-3">
                  <div className="flex flex-wrap items-start justify-between gap-2">
                    <div className="min-w-0">
                      <div className="text-sm font-medium text-text-primary">{c.title}</div>
                      <div className="truncate font-mono text-xs text-text-muted">{c.sha256}</div>
                      <div className="mt-0.5 text-micro text-text-muted">{c.fileType} · owner {c.ownerUserId} · tenant {c.tenantId}</div>
                      {c.description && <div className="mt-1 text-xs text-text-secondary">{c.description}</div>}
                    </div>
                    <div className="flex shrink-0 gap-2">
                      <Button size="sm" disabled={busy === c.id} onClick={() => void act(c.id, () => api.adminModerate(c.id, 'approve'))}>Approve</Button>
                      <Button size="sm" variant="secondary" disabled={busy === c.id} onClick={() => void act(c.id, () => api.adminModerate(c.id, 'request-changes', { note: window.prompt('Changes requested note:') ?? undefined }))}>Changes</Button>
                      <Button size="sm" variant="secondary" disabled={busy === c.id} onClick={() => void act(c.id, () => api.adminModerate(c.id, 'reject', { note: window.prompt('Rejection note:') ?? undefined }))}>Reject</Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Verified researchers</CardTitle>
        </CardHeader>
        <CardContent>
          {!researchers ? (
            <Skeleton className="h-16 w-full" />
          ) : researchers.length === 0 ? (
            <div className="py-6 text-center text-sm text-text-muted">No verified-researcher applications yet.</div>
          ) : (
            <div className="divide-y divide-white/5">
              {researchers.map((r) => (
                <div key={r.userId} className="flex flex-wrap items-center justify-between gap-2 py-3">
                  <div className="min-w-0">
                    <div className="text-sm font-medium text-text-primary">{r.userId}</div>
                    <div className="text-micro text-text-muted">status: {r.status}{r.decisionNote ? ` · ${r.decisionNote}` : ''}</div>
                    {r.statement && <div className="mt-1 line-clamp-2 text-xs text-text-secondary">{r.statement}</div>}
                  </div>
                  <div className="flex shrink-0 gap-2">
                    {r.status !== 'approved' && (
                      <Button size="sm" disabled={busy === r.userId} onClick={() => void act(r.userId, () => api.adminResearcherDecision(r.userId, 'grant'))}>Grant</Button>
                    )}
                    {r.status === 'pending' && (
                      <Button size="sm" variant="secondary" disabled={busy === r.userId} onClick={() => void act(r.userId, () => api.adminResearcherDecision(r.userId, 'reject', window.prompt('Rejection note:') ?? undefined))}>Reject</Button>
                    )}
                    {r.status === 'approved' && (
                      <Button size="sm" variant="secondary" disabled={busy === r.userId} onClick={() => void act(r.userId, () => api.adminResearcherDecision(r.userId, 'revoke', window.prompt('Revoke reason:') ?? undefined))}>Revoke</Button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Abuse &amp; suspension</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="mb-3 flex flex-wrap items-center gap-2">
            <SuspendUserControl busy={!!busy} onSuspend={(userId, reason) => void act(userId, () => api.adminSuspendCommunity(userId, reason))} />
          </div>
          {!access ? (
            <Skeleton className="h-16 w-full" />
          ) : access.length === 0 ? (
            <div className="py-6 text-center text-sm text-text-muted">No suspended or flagged users.</div>
          ) : (
            <div className="divide-y divide-white/5">
              {access.map((a) => (
                <div key={a.userId} className="flex flex-wrap items-center justify-between gap-2 py-3">
                  <div className="min-w-0">
                    <div className="text-sm font-medium text-text-primary">{a.userId}</div>
                    <div className="text-micro text-text-muted">
                      {a.suspended ? <span className="text-status-danger">suspended{a.suspendedReason ? `: ${a.suspendedReason}` : ''}</span> : 'active'}
                      {a.abuseFlagged && <span className="ml-2 text-yellow-400">flagged{a.abuseReason ? `: ${a.abuseReason}` : ''}</span>}
                    </div>
                  </div>
                  <div className="flex shrink-0 gap-2">
                    {a.suspended ? (
                      <Button size="sm" disabled={busy === a.userId} onClick={() => void act(a.userId, () => api.adminRestoreCommunity(a.userId))}>Restore</Button>
                    ) : (
                      <Button size="sm" variant="secondary" disabled={busy === a.userId} onClick={() => void act(a.userId, () => api.adminSuspendCommunity(a.userId, window.prompt('Suspend reason:') ?? 'admin suspension'))}>Suspend</Button>
                    )}
                    {a.abuseFlagged && (
                      <Button size="sm" variant="ghost" disabled={busy === a.userId} onClick={() => void act(a.userId, () => api.adminClearCommunityFlag(a.userId))}>Clear flag</Button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function SuspendUserControl({ busy, onSuspend }: { busy: boolean; onSuspend: (userId: string, reason: string) => void }) {
  const [userId, setUserId] = useState('');
  const [reason, setReason] = useState('');
  return (
    <>
      <input className={INPUT_CLS + ' w-48'} value={userId} onChange={(e) => setUserId(e.target.value)} placeholder="user id to suspend" />
      <input className={INPUT_CLS + ' w-56'} value={reason} onChange={(e) => setReason(e.target.value)} placeholder="reason" />
      <Button size="sm" variant="secondary" disabled={busy || !userId.trim()} onClick={() => { onSuspend(userId.trim(), reason.trim() || 'admin suspension'); setUserId(''); setReason(''); }}>Suspend</Button>
    </>
  );
}
